import { Snackbar as MUISnackBar } from '@mui/material';
import Alert from '@mui/material/Alert';
/**
 * A customizable Snackbar component that wraps Material-UI's Snackbar with an Alert.
 *
 * @param {Object} props - The component props
 * @param {boolean} props.open - Controls whether the snackbar is visible
 * @param {string} props.message - The message to display in the snackbar
 * @param {('error'|'warning'|'info'|'success')} [props.status='info'] - The severity/status of the alert
 * @param {number} [props.duration=3000] - Auto-hide duration in milliseconds (null to disable auto-hide)
 * @param {function} props.onClose - Callback fired when the snackbar is closed
 * @param {Object} [props.anchorOrigin] - Position of the snackbar on the screen
 * @param {('top'|'bottom')} [props.anchorOrigin.vertical='bottom'] - Vertical position
 * @param {('left'|'center'|'right')} [props.anchorOrigin.horizontal='left'] - Horizontal position
 * @param {('filled'|'outlined'|'standard')} [props.variant='filled'] - The variant of the alert
 * @param {boolean} [props.showCloseButton=true] - Whether to show the close button on the alert
 * @param {boolean} [props.requiresAction=false] - When true, forces user to click close button (disables auto-hide and enforces close button)
 * @param {Object} [props.snackbarProps] - Additional props to pass to the MUI Snackbar component
 * @param {Object} [props.alertProps] - Additional props to pass to the MUI Alert component
 * @param {Object} [props.sx] - Custom sx props for styling the Alert component
 *
 * @returns {JSX.Element} The rendered Snackbar component
 *
 * @example
 * // Basic usage
 * <Snackbar
 *   open={isOpen}
 *   message="Operation successful!"
 *   status="success"
 *   onClose={handleClose}
 * />
 *
 * @example
 * // Custom positioning and duration
 * <Snackbar
 *   open={isOpen}
 *   message="Error occurred"
 *   status="error"
 *   duration={5000}
 *   anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
 *   onClose={handleClose}
 * />
 *
 * @example
 * // No auto-hide with outlined variant
 * <Snackbar
 *   open={isOpen}
 *   message="Important notification"
 *   status="warning"
 *   duration={null}
 *   variant="outlined"
 *   onClose={handleClose}
 * />
 *
 * @example
 * // Requires user action - no auto-hide, must click close
 * <Snackbar
 *   open={isOpen}
 *   message="Critical: Please review before continuing"
 *   status="error"
 *   requiresAction={true}
 *   onClose={handleClose}
 * />
 */
export const Snackbar = ({
  open,
  message,
  status = 'info',
  duration = 3000,
  onClose,
  anchorOrigin,
  variant = 'filled',
  showCloseButton = true,
  requiresAction = false,
  snackbarProps = {},
  alertProps = {},
  sx = {},
}) => {
  const defaultAnchorOrigin = { vertical: 'bottom', horizontal: 'left' };

  // When requiresAction is true, override duration and ensure close button shows
  const effectiveDuration = requiresAction ? null : duration;
  const effectiveShowCloseButton = requiresAction ? true : showCloseButton;

  return (
    <MUISnackBar
      open={open}
      autoHideDuration={effectiveDuration}
      onClose={onClose}
      anchorOrigin={anchorOrigin || defaultAnchorOrigin}
      {...snackbarProps}
    >
      <Alert
        onClose={effectiveShowCloseButton ? onClose : undefined}
        severity={status}
        variant={variant}
        sx={{ width: '100%', ...sx }}
        {...alertProps}
      >
        {message}
      </Alert>
    </MUISnackBar>
  );
};
